import axios from "axios";

export const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || "http://localhost:3030",
});

// attach token if exists
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem("authToken");
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        // Dev fallback: when the backend runs with JWT_ENABLED=false the
        // verifyToken middleware skips and req.user is empty. Surface the
        // logged-in admin's identity via headers so admin-gated endpoints
        // (e.g. /api/admin/insights/*) don't 403 in dev. SYT-Admin only
        // hosts admin users — if userData exists, the user is an admin.
        try {
            const raw = localStorage.getItem("userData");
            if (raw) {
                const userData = JSON.parse(raw);
                if (userData?.admin_id) {
                    config.headers["x-user-id"] = userData.admin_id;
                    config.headers["x-user-type"] = "admin";
                }
            }
        } catch (_) { /* ignore */ }
        return config;
    },
    (error) => Promise.reject(error)
);

export async function postData(url, payload) {
    const config =
        payload instanceof FormData
            ? { headers: { "Content-Type": "multipart/form-data" } }
            : {};
    const res = await api.post(url, payload, config);
    return res.data;
}

export async function getData(url, params) {
    const res = await api.get(url, { params });
    return res.data;
}

export async function putData(url, payload) {
    const config =
        payload instanceof FormData
            ? { headers: { "Content-Type": "multipart/form-data" } }
            : {};

    const res = await api.put(url, payload, config);
    return res.data;
}

export const API_ENDPOINTS = {
    sendOtp: "/api/admin/sendOTPAdmin",
    verifyOtpLogin: "/api/admin/verifyOTPLoginAdmin",
    getUsersList: "/api/customer/getAllUsers",
    getAdminById: "/api/admin/getAdminById",
    deleteMultipleUsers: "/api/customer/deleteCustomer",
    getTruckOwners: "/api/truckOwner/getAllTruckOwners",
    deleteTruckOwners: "/api/truckOwner/deleteTruckOwner",
    getTrucks: "/api/truckDetails/getAllTrucks",
    updateAdmin: "/api/admin/updateUser",
    getDriversList: "/api/driver/getAllDrivers",
    dashboardSummary: "/api/dashboard/dashboardSummary",
    allTrips: "/api/trip/getAllTrips",
    insertCityData: "/api/region/createCity",
    getCitiesData: "/api/region/getAllCities",
    insertBoardingDropPoint: "/api/masterBoardingDropPoints/createBoardingDropPoint",
    markOnDuty: "/api/driver/updateDriverOnDutyStatus",
    fetchTripById: "/api/trip/getAdminTripById",
    tripAnalytics: "/api/dashboard/tripsChart",
    createDeliveryZone: "/api/deliveryZones/createCityDeliveryZone",
    getDeliveryZones: "/api/deliveryZones/getAllCityDeliveryZones",
    getAllCharges: "/api/charges/getAllCharges",
    updateCharge: "/api/charges/updateCharges",
    getPackages: "/api/packageManagement/getAllPackageSizes",
    createPackages: "/api/packageManagement/createPackageSize",
    updatePackages: "api/packageManagement/updatePackageSize",
    deletePackages: "/api/packageManagement/deletePackageSize",
    getTruckOwnerById:   "/api/truckOwner/getAdminTruckOwnerById",
    ownerInsightsSummary:"/api/insights/owner-summary",
    getRevenueChart: "/api/dashboard/revenueChart",
    createOwnerPayment: "/api/initiatedPayments/initiateOwnerPayment",
    createBoardingPoints: "/api/masterBoardingDropPoints/createBoardingDropPoint",
    getOwnerPayments: "/api/initiatedPayments/getAllOwnerPayments",
    // OPH-4 — owner-payout reconciliation report (admin only)
    reconciliationReport: "/api/initiatedPayments/admin/reconciliation",
    // OPH-4 follow-up — per-trip owed breakdown for the Pay drill-down on
    // an "Owed" reconciliation row (admin only)
    ownerTripsOwed: "/api/initiatedPayments/admin/trips-owed/:owner_id",
    // ─── OWO-001 — Owner Withdrawal (offline) ───────────────────────────
    adminWithdrawalsList: "/api/admin/withdrawRequests",
    adminWithdrawalClaim: "/api/admin/withdrawRequests/:id/claim",
    adminWithdrawalComplete: "/api/admin/withdrawRequests/:id/complete",
    adminWithdrawalFail: "/api/admin/withdrawRequests/:id/fail",
    adminWithdrawalsTdsExport: "/api/admin/withdrawRequests/tds-export",
    payoutDestinationsByOwner: "/api/payoutDestinations/by-owner/:owner_id",
    payoutDestinationsAdminList: "/api/payoutDestinations/admin/list",
    payoutDestinationAdminVerify: "/api/payoutDestinations/:id/admin-verify",
    payoutDestinationAdminReject: "/api/payoutDestinations/:id/admin-reject",
    generateCharges: "/api/charges/createCharges",
    bulkUpsertCharges: "/api/charges/bulkUpsert",
    pricingKpis: "/api/dashboard/pricingKpis",
    pricingSourceChart: "/api/dashboard/pricingSourceChart",
    demandMultiplierDistribution: "/api/dashboard/demandMultiplierDistribution",
    routeDemandLeaderboard: "/api/dashboard/routeDemandLeaderboard",
    fetchDriverById: "/api/driver/getDriverById/:driver_id",
    getTruckDetailsById: "/api/truckDetails/getTruckByIdForAdmin/:truck_id",
    findCustomerById: "/api/customer/getCustomerById",
    fetchPackagesByCustomer: "/api/package/getAllPackagesByCustomerId",
    fetchAllPackages: "/api/package/getAllPackages",
    fetchPackagesByTrip:      "/api/package/getAllPackageRequestByTripId",
    adminTripPackages:        "/api/package/admin/tripPackages",
    fetchPackageById: "/api/package/getPackageById",
    cancelPackageMappingByAdmin: "/api/package/admin/cancelPackageMapping",

    // ─── Complaints & Dispute Management (CCDM-001 / CMP-002) ─────────────────
    getAllComplaints: "/api/complaints",
    getComplaintStats: "/api/complaints/stats",
    getComplaintById: "/api/complaints/:complaint_id",
    triageComplaint: "/api/complaints/:complaint_id/triage",
    // Legacy single-shot — kept until the detail page fully migrates.
    requestEvidence: "/api/complaints/:complaint_id/request-evidence",
    // CMP-002 — n-round, multi-target evidence/details request.
    // Body: { target: "customer" | "owner_driver" | "both", message }
    requestComplaintDetails: "/api/complaints/:complaint_id/request-details",
    getComplaintAuditLog: "/api/complaints/:complaint_id/audit-log",
    getComplaintRequests: "/api/complaints/:complaint_id/requests",
    resolveComplaint: "/api/complaints/:complaint_id/resolve",

    getAllBoardingPoints: "/api/masterBoardingDropPoints/getAllBoardingDropPoints",
    updateCity: "/api/region/updateCity",
    updateBoardingPointStatus: "/api/masterBoardingDropPoints/boardingdrop",

    // ─── GPS Tracking (GTM-001) ────────────────────────────────────────────────
    adminFleet: "/api/gps/adminFleet",
    gpsLocationHistory: "/api/gps/locationHistory",

    // ─── Cargo Protection Module (CPM-001) ────────────────────────────────────
    getProtectionClaims: "/api/protection/claims",
    getProtectionClaimStats: "/api/protection/stats",
    getProtectionClaimById: "/api/protection/claims/:claim_id/detail",
    triageProtectionClaim: "/api/protection/claims/:claim_id/triage",
    requestClaimEvidence: "/api/protection/claims/:claim_id/request-evidence",
    approveProtectionClaim: "/api/protection/claims/:claim_id/approve",
    rejectProtectionClaim: "/api/protection/claims/:claim_id/reject",
    markClaimFraudulent: "/api/protection/claims/:claim_id/mark-fraudulent",
    getProtectionFund: "/api/protection/fund",
    getProtectionFundLedger:  "/api/protection/fund/ledger",
    adminGetAllProtections:   "/api/protection/admin/bookings",
    updateProtectionConfig: "/api/protection/config",

    // ─── Claim Ratings ────────────────────────────────────────────────────────
    getClaimRatings: "/api/claim-ratings/:claimId/ratings",
    submitClaimRating: "/api/claim-ratings/:claimId/rating",
    getRatingAnalytics: "/api/claim-ratings/analytics",

    // ─── AI Insights Ops (Phase 3.C) ──────────────────────────────────────────
    adminInsightsAskStats: "/api/admin/insights/ask-stats",
    adminInsightsAskLogs: "/api/admin/insights/ask-logs",
    adminInsightsConfig: "/api/admin/insights/config",
    adminInsightsConfigUpdate: "/api/admin/insights/config/:key",

    // ─── AI Booking Ops (Module G) ────────────────────────────────────────────
    adminBookingStats: "/api/admin/booking/stats",
    adminBookingLogs: "/api/admin/booking/logs",

    // ─── Discount & Incentive Module (DIM-001) ────────────────────────────────
    dimConfig: "/api/dim/admin/config",
    dimCampaigns: "/api/dim/admin/campaigns",
    dimCampaignById: "/api/dim/admin/campaigns/:id",
    dimCampaignActivate: "/api/dim/admin/campaigns/:id/activate",
    dimCampaignPause: "/api/dim/admin/campaigns/:id/pause",
    dimCampaignResume: "/api/dim/admin/campaigns/:id/resume",
    dimCampaignBudgetTopup: "/api/dim/admin/campaigns/:id/budget-topup",
    dimCampaignRedemptions: "/api/dim/admin/campaigns/:id/redemptions",
    dimCampaignAnalytics: "/api/dim/admin/campaigns/:id/analytics",

    // Phase 3 — Loyalty admin
    dimLoyaltyCustomers: "/api/dim/admin/loyalty/customers",
    dimLoyaltyOverride: "/api/dim/admin/loyalty/override",
    dimLoyaltyHistory: "/api/dim/admin/loyalty/history/:customer_id",
    dimLoyaltyEvalNow: "/api/dim/admin/loyalty/eval-now",

    // Phase 4 — Referral admin
    dimReferrals: "/api/dim/admin/referrals",
    dimReferralApprove: "/api/dim/admin/referrals/:id/approve",
    dimReferralReject: "/api/dim/admin/referrals/:id/reject",
    dimReferralSweepNow: "/api/dim/admin/referrals/sweep-now",

    // ─── In-app chat (admin read-only audit) ───────────────────────────
    chatAdminThreads: "/api/chat/admin/threads",
    chatAdminThreadMessages: "/api/chat/admin/threads/:thread_id/messages",

    // ─── PRC-001 Pricing Tiers ─────────────────────────────────────────
    pricingWeightTiers: "/api/admin/pricingTiers/weightTiers",
    pricingWeightTierUpdate: "/api/admin/pricingTiers/weightTiers/:id",
    pricingHandlingTiers: "/api/admin/pricingTiers/handlingTiers",
    pricingHandlingTierUpdate: "/api/admin/pricingTiers/handlingTiers/:id",

    // ─── E-Way Bill Compliance (EWB-001) ──────────────────────────────
    ewbDecisionConfig: "/api/ewb/config/decision",
    ewbAdminExemptGoods: "/api/ewb/admin/exempt-goods",
    ewbAdminStateThresholds: "/api/ewb/admin/state-thresholds",
    ewbAdminBookings: "/api/ewb/admin/bookings",
    ewbAdminBookingAudit: "/api/ewb/admin/bookings/:mapping_id/audit",
    ewbAdminComplianceDashboard: "/api/ewb/admin/compliance-dashboard",
    ewbOwnerTripCompliance: "/api/ewb/owner/trip/:trip_id/compliance",
    ewbOwnerTripBookings:   "/api/ewb/owner/trip/:trip_id/bookings",

    // ─── Agent Role (ARM-001) ───────────────────────────────────────
    adminGetAllAgents:        "/api/agent/admin/getAllAgents",
    adminGetAgentById:        "/api/agent/admin/getAgentById",
    adminSetAgentStatus:      "/api/agent/admin/setAgentStatus",
    adminAgentBookings:       "/api/agent/admin/agentBookings",
    adminAgentWallet:         "/api/agent/admin/agentWallet",
    adminAgentFleet:          "/api/agent/admin/agentFleet",

    // ─── HCT-001 (Heavy Cargo Threshold) ─────────────────────────────
    heavyCargoEnquiries:      "/api/package/admin/heavy-cargo-enquiries",
    updateHeavyCargoEnquiry:  "/api/package/admin/heavy-cargo-enquiry",
};
